/*
 * PageWright — native search/collection click-and-search analytics (Phase 2A, 2026-07-20).
 *
 * Covers the two native, server-rendered surfaces pw-image-search.js's own beacons don't reach:
 *   - /search            (search-results.liquid)  -> 'search' / 'zero_results' / 'view_item_list'
 *   - /collections/*     (main-collection.liquid, pw-collaborations.liquid, pw-projects.liquid,
 *                          etc.)                   -> 'view_item_list' only (no query to report)
 *
 * Posts to the SAME public beacon (@pagewright/api's /api/collect -> Neon cs_analytics) pw-
 * image-search.js already uses, via the same fire-and-forget navigator.sendBeacon pattern (that
 * endpoint's own design intent). Rendered site-wide (theme.liquid), but every listener below
 * gates on the current route so it's a true no-op everywhere else.
 *
 * Deliberately does NOT touch snippets/product-card.liquid (shared across many non-search/
 * collection surfaces — home page, upsells, etc. — so a shared-file edit would be higher risk
 * for no benefit here). Instead the product handle is derived client-side from the `href` of the
 * existing `.product-card__link` anchor every real product card already renders.
 */
(function () {
  'use strict';

  // PageWright's production backend. This asset has no per-page settings slot to swap via
  // scripts/point-templates.sh (that script only rewrites template JSON block settings) — for a
  // one-off staging check, temporarily hand-edit this constant and discard the diff before
  // committing/pushing, same convention that script's own header documents for template swaps.
  var BACKEND_URL = (window.PW_BACKEND_URL || '')  // set once by layout/theme.liquid from the
  // per-theme `backend_url` setting. Blank DISABLES backend calls rather than falling back
  // to production — a staging theme silently writing to prod data is the bug this replaces.

  var PW_SESSION_KEY = 'pw_session_id';

  function pwSessionId() {
    try {
      var id = sessionStorage.getItem(PW_SESSION_KEY);
      if (!id) {
        id = 'pw_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(PW_SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      return undefined;
    }
  }

  function pwGaClientId() {
    var m = /(?:^|;\s*)_ga=GA1\.\d\.(\d+\.\d+)/.exec(document.cookie || '');
    return m ? m[1] : undefined;
  }

  function postCollectEvent(payload) {
    try {
      var body = JSON.stringify(Object.assign(
        { eventVersion: 1, sessionId: pwSessionId(), clientId: pwGaClientId() },
        payload
      ));
      var url = BACKEND_URL + '/api/collect';
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics beacon must never break the page */
    }
  }

  function productHandleFromHref(href) {
    if (!href) return undefined;
    var m = /\/products\/([^/?#]+)/.exec(href);
    return m ? decodeURIComponent(m[1]) : undefined;
  }

  function isSearchRoute() {
    return location.pathname === '/search';
  }

  function isCollectionRoute() {
    return location.pathname.indexOf('/collections/') === 0;
  }

  // Only real, rendered cards — matches the same selector pw-image-search.js's applySearchAzSort
  // already relies on for "how many products are actually on screen" (data-product-id, no
  // onboarding/placeholder card).
  function countRealProductCards() {
    return document.querySelectorAll('product-card[data-product-id]:not([data-placeholder])').length;
  }

  function fireLoadEvents() {
    var searching = isSearchRoute();
    var browsing = isCollectionRoute();
    if (!searching && !browsing) return;

    var query = searching ? (new URLSearchParams(location.search).get('q') || '').trim() : '';
    var count = countRealProductCards();

    if (searching && query && count === 0) {
      postCollectEvent({ event: 'zero_results', query: query });
      return;
    }
    if (searching && query) {
      postCollectEvent({ event: 'search', query: query, resultCount: count });
    }
    postCollectEvent(query ? { event: 'view_item_list', query: query, resultCount: count }
                           : { event: 'view_item_list', resultCount: count });
  }

  document.addEventListener('click', function (e) {
    if (!isSearchRoute() && !isCollectionRoute()) return;
    var link = e.target.closest('.product-card__link');
    if (!link) return;
    var handle = productHandleFromHref(link.getAttribute('href'));
    if (handle) postCollectEvent({ event: 'select_item', handle: handle });
  });

  // Phase 2B (2026-07-28): begin_checkout. Reachable from the cart page/drawer on ANY route
  // (not gated to search/collection like the listener above) -- the native checkout submit
  // button (snippets/cart-summary.liquid's #checkout, name="checkout") posts the cart form
  // directly to Shopify's checkout with no client-side navigation intercept, so this fires via
  // sendBeacon specifically because it must complete before the page unloads. No handle -- a
  // cart can have multiple lines, begin_checkout is a cart-level signal, not tied to one product
  // (matches the contract's optional handle field).
  document.addEventListener('click', function (e) {
    if (e.target.closest('[name="checkout"]')) {
      postCollectEvent({ event: 'begin_checkout' });
    }
  });

  /*
   * generate_lead for the NATIVE Shopify newsletter forms (2026-07-29).
   *
   * Why: Google Ads' "Submit lead form" conversion action was triggering on a Google Tag
   * page-load rule — "visits a page starting with /pages" — i.e. counting every /pages/...
   * PAGEVIEW as a lead. Useless for bidding, and actively harmful if set Primary.
   *
   * The freebie-claim form (what both Winter2026 ads point at) already fires generate_lead
   * correctly from inside its success branch — see assets/pw-freebie-claim.js. The gap was the
   * two native {% form 'customer' %} newsletter signups (home band + footer), which emitted
   * nothing. Those are full-page POSTs: Shopify redirects back with ?customer_posted=true.
   * Firing the same event here means all three lead surfaces emit ONE consistent signal, and
   * the Ads conversion can be scoped to the event instead of a URL pattern.
   *
   * Chose an event over a dedicated /pages/thank-you-leads URL deliberately: a thank-you page
   * can be reached by direct navigation, refresh, back-button or a shared link, every one of
   * which inflates conversions — the same over-counting problem we are removing.
   *
   * The sessionStorage guard matters for the same reason: ?customer_posted=true SURVIVES a
   * refresh, so an unguarded handler would re-fire on every reload of that URL.
   */
  function fireNewsletterLead() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') !== 'true') return;

    // One event per submission, not per page view of the redirected URL.
    var key = 'pw_lead_fired:' + window.location.pathname + window.location.search;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {
      /* private mode / storage disabled → fall through and fire once per load rather than never */
    }

    if (typeof window.gtag !== 'function') return; // ad-blocked or GA4 app absent — stay silent
    window.gtag('event', 'generate_lead', {
      method: 'newsletter',
      form_source: 'shopify_customer_form',
      page_path: window.location.pathname,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      fireLoadEvents();
      fireNewsletterLead();
    });
  } else {
    fireLoadEvents();
    fireNewsletterLead();
  }
})();
